import { useState, type ReactNode } from 'react'

export type TableData = { columns: string[]; rows: (string | number)[][]; numeric?: boolean[] }

/**
 * A dashboard card: title, subtitle, the chart, and a "View as table" switch
 * so every number can be read exactly, without hovering.
 */
export default function ChartCard({
  title,
  subtitle,
  caption,
  table,
  className = '',
  children,
}: {
  title: string
  subtitle?: string
  /** A one-line takeaway shown above the chart, e.g. "Roads & Transport is the largest block". */
  caption?: ReactNode
  table?: TableData
  className?: string
  children: ReactNode
}) {
  const [asTable, setAsTable] = useState(false)

  return (
    <section className={`card chart-card ${className}`}>
      <header className="chart-head">
        <div>
          <h2 className="chart-title">{title}</h2>
          {subtitle && <p className="chart-subtitle">{subtitle}</p>}
        </div>
        {table && (
          <button className="link-button small" onClick={() => setAsTable(!asTable)} aria-pressed={asTable}>
            {asTable ? 'View as chart' : 'View as table'}
          </button>
        )}
      </header>
      {caption && <p className="chart-caption">{caption}</p>}

      {asTable && table ? (
        <table className="table compact">
          <thead>
            <tr>
              {table.columns.map((c, i) => (
                <th key={c} className={table.numeric?.[i] ? 'num' : ''}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, r) => (
              <tr key={r}>
                {row.map((cell, i) => (
                  <td key={i} className={table.numeric?.[i] ? 'num' : ''}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        children
      )}
    </section>
  )
}
